import { ReactNode } from 'react';
import { SlideAction, ViewMode } from './enums';

export interface ColumnData {
  title: string;
  /**
   * Optional pre-rendered title that bypasses i18n translation. Use this when
   * the title is dynamic content (e.g. an asset name) rather than a translation
   * key. When provided, takes precedence over `title`.
   */
  rawTitle?: string;
  count?: number;
  mainComponent: ReactNode;
  headerComponent?: ReactNode;
  /**
   * Inline content rendered immediately to the right of the panel title (e.g. a
   * list/calendar view selector). Passed through to `PanelHeader.titleAccessory`;
   * sits snug to the title and never clips the right-side `headerComponent`.
   */
  titleAccessory?: ReactNode;
  showHeader?: boolean;
  showBackButton?: boolean;
  backButtonTransition?: SlideAction;
  /**
   * Controls when `onBackButtonPress` fires relative to `backButtonTransition`.
   * Default `await` runs the transition in the current layout before navigating.
   * `concurrent` hands the current column to the next SlideLayout so route exit
   * and route enter panels can animate together.
   */
  backButtonTransitionBehavior?: 'await' | 'concurrent';
  /**
   * For concurrent route handoffs, choose which visible column is captured as
   * the outgoing panel. When omitted, SlideLayout chooses the outgoing visible
   * column for the requested transition.
   */
  backButtonTransitionExitColumn?: 'left' | 'right' | 'both';
  onBackButtonPress?: () => void;
  /**
   * Optional async guard called before the back button transition animates.
   * Return `true` to allow the transition, `false` to block it.
   * When defined, `onBackButtonPress` is still called afterward if the guard resolves `true`.
   * Existing columns without this field are unaffected.
   */
  backButtonGuard?: () => Promise<boolean>;
  /** When true, the column content won't be wrapped in a ScrollView (useful for components with internal scroll) */
  disableScroll?: boolean;
  debug?: PanelDebugMeta;
}

export type PanelDebugEntity = {
  type: string;
  id?: string;
  label?: string;
};

export type PanelDebugSourceLocation = {
  file: string;
  line?: number;
  column?: number;
  symbol?: string;
};

export type PanelDebugSourceMap = {
  owner?: PanelDebugSourceLocation;
  column?: PanelDebugSourceLocation;
  component?: PanelDebugSourceLocation;
  route?: PanelDebugSourceLocation;
};

export type PanelDebugMeta = {
  id: string;
  titleKey?: string;
  rawTitle?: string;
  component?: string;
  componentSource?: string;
  columnSource?: string;
  ownerComponent?: string;
  ownerSource?: string;
  routePanelKey?: string;
  routePattern?: string;
  routeSource?: string;
  source?: PanelDebugSourceMap;
  entity?: PanelDebugEntity;
};

export type PanelDebugRouteMatch = {
  id?: string;
  pathname: string;
  params?: Record<string, string | undefined>;
  panelKey?: string;
  panelTitle?: string;
  panelTransitionKey?: string;
  routePattern?: string;
  routeSource?: string;
  routeElement?: string;
  routeElementSource?: string;
  shellComponent?: string;
  shellSource?: string;
};

export type PanelRefDebug = {
  id: string;
  title?: string;
  rawTitle?: string;
  count?: number;
  titleKey?: string;
  component?: string;
  componentSource?: string;
  columnSource?: string;
  ownerComponent?: string;
  ownerSource?: string;
  routePanelKey?: string;
  routePattern?: string;
  routeSource?: string;
  source?: PanelDebugSourceMap;
  entity?: PanelDebugEntity;
};

export type PanelVisualPosition =
  | 'left'
  | 'right'
  | 'hidden'
  | 'offscreen-left'
  | 'offscreen-right';

export type PanelSlotDebug = {
  panel: PanelRefDebug;
  visualPosition: PanelVisualPosition;
  visible: boolean;
  widthPercent?: number;
  opacity?: number;
  translateXPercent?: number;
  element?: PanelDebugElement;
};

export type PanelDebugRect = {
  x: number;
  y: number;
  top: number;
  right: number;
  bottom: number;
  left: number;
  width: number;
  height: number;
  centerX: number;
  centerY: number;
};

export type PanelDebugScrollMetrics = {
  scrollLeft: number;
  scrollTop: number;
  scrollWidth: number;
  scrollHeight: number;
  clientWidth: number;
  clientHeight: number;
  maxScrollLeft: number;
  maxScrollTop: number;
  canScrollX: boolean;
  canScrollY: boolean;
};

export type PanelDebugScrollableElement = PanelDebugScrollMetrics & {
  index: number;
  tagName: string;
  selectorHint: string;
  id?: string;
  testID?: string;
  role?: string;
  ariaLabel?: string;
  className?: string;
  rect?: {
    viewport: PanelDebugRect;
    page: PanelDebugRect;
    relativeToLayout?: PanelDebugRect;
    relativeToPanel?: PanelDebugRect;
    visibleViewport: PanelDebugRect;
    visibleViewportRatio: number;
  };
};

export type PanelDebugElement = {
  testID: string;
  selector: string;
  found: boolean;
  missingReason?: 'document-unavailable' | 'element-not-found';
  rect?: {
    viewport: PanelDebugRect;
    page: PanelDebugRect;
    relativeToLayout?: PanelDebugRect;
    visibleViewport: PanelDebugRect;
    visibleViewportRatio: number;
  };
  computedStyle?: {
    display: string;
    visibility: string;
    opacity: string;
    pointerEvents: string;
    position: string;
    zIndex: string;
    transform: string;
  };
  scroll?: PanelDebugScrollMetrics;
  scrollables?: PanelDebugScrollableElement[];
};

export type PanelDebugViewport = {
  width: number;
  height: number;
  scrollX: number;
  scrollY: number;
  devicePixelRatio: number;
  documentWidth: number;
  documentHeight: number;
};

export type GrippPanelDebugState = {
  version: 1;
  capturedAt: string;
  url: {
    pathname: string;
    search?: string;
    hash?: string;
  };
  router: {
    transitionKey?: string;
    matches?: PanelDebugRouteMatch[];
    params?: Record<string, string | undefined>;
  };
  layout: {
    currentViewMode: ViewMode;
    requestedViewMode?: ViewMode;
    hasRightColumn: boolean;
    hasThirdColumn: boolean;
    hasFourthColumn: boolean;
    isThirdPanelVisible: boolean;
    isFourthPanelVisible: boolean;
  };
  slots: {
    left?: PanelSlotDebug;
    right?: PanelSlotDebug;
    third?: PanelSlotDebug;
    fourth?: PanelSlotDebug;
    exitingLeft?: PanelSlotDebug;
    exitingRight?: PanelSlotDebug;
  };
  deferred?: {
    right?: PanelRefDebug;
    third?: PanelRefDebug;
    fourth?: PanelRefDebug;
  };
  browser?: {
    viewport: PanelDebugViewport;
    layoutElement: PanelDebugElement;
  };
  codeSearchHints?: {
    exactFilesToOpen: string[];
    exactIdsToSearch: string[];
  };
  debug?: {
    layoutInstanceId?: string;
  };
};

export type SlideLayoutInitialTransition =
  | SlideAction.NEXT
  | SlideAction.PREV
  | SlideAction.DUAL;

export type SlideLayoutLocationState = {
  slideLayoutInitialTransition?: SlideLayoutInitialTransition;
};

// Context for passing click handlers and navigation to column content
export interface SlideLayoutContextType {
  /** Which column this component is rendered in */
  column: 'left' | 'right' | 'third' | 'fourth';
  /**
   * Current view mode (SINGLE or DUAL)
   */
  currentViewMode: ViewMode;
  /**
   * Execute a slide action and await for animation to complete
   * @param action - The slide action to execute (SINGLE, DUAL, NEXT, PREV, or default for fade in)
   * @returns Promise that resolves when the animation completes
   */
  onSlideChange: (action: SlideAction) => Promise<void>;
  /**
   * Capture the currently visible edge column for a route transition that will
   * be animated by the destination layout's `initialTransition`.
   */
  prepareSlideTransition: (action: SlideLayoutInitialTransition) => void;
  /**
   * Set a custom title for a column by its id. Pass undefined to reset to default.
   * WARNING: Call this method only after the column has been rendered to avoid title reset on re-render.
   * @param title - New title for the column
   */
  setColumnTitle: (title: string | undefined) => void;
  setColumnCount: (count: number | undefined) => void;
  /**
   * Set a full-column overlay for this column (e.g. unsaved changes sheet).
   * Pass null to clear.
   */
  setColumnOverlay: (overlay: ReactNode | null) => void;
  /**
   * Toggle the third (hidden) panel. When shown, the left panel exits, the
   * right panel shifts left to become the new left, and the third panel enters
   * as the new right. Calling again reverses the transition.
   */
  toggleThirdPanel: () => void;
}

export type SlidePanelSlot = 'right' | 'third' | 'fourth';

/**
 * Baked into the outlet context when SlideLayout renders its own `<Outlet />`
 * (see `enableChildRoutes`). Child route elements call `useSlidePanel(...)`,
 * which reads this registrar from `useOutletContext()` and registers the
 * column for its slot. Apps extend this type with their own fields via the
 * `outletContext` prop.
 */
export interface SlidePanelRegistrar {
  setSlidePanel: (slot: SlidePanelSlot, column: ColumnData | null) => void;
}

export interface SlideLayoutProps {
  leftColumn: ColumnData;
  /**
   * When set, this prop takes precedence over any column registered via
   * `useSlidePanel` (e.g. AssetsShell's selection panel eclipses the
   * mounted route panel until the shell clears its prop). When omitted,
   * the outlet-registered column is used.
   */
  rightColumn?: ColumnData;
  thirdColumn?: ColumnData;
  /**
   * Optional fourth column that slides in from the right when the third column
   * is already open. Used for sub-editors opened from the third column (e.g. the
   * group editor opened from the asset edit form).
   *
   * API constraint: consumers must set `showBackButton: true` and
   * `onBackButtonPress` — there is no imperative `toggleFourthPanel` fallback.
   */
  fourthColumn?: ColumnData;
  /**
   * When omitted, derived as DUAL if any right column (prop or outlet-contributed)
   * is present, SINGLE otherwise.
   */
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
  /**
   * Run a one-time enter animation on mount. Use for URL transitions where the
   * previous route cannot keep the same SlideLayout instance mounted.
   */
  initialTransition?: SlideLayoutInitialTransition;
  /**
   * Encodes the active route panel stack. Depth changes while in DUAL mode
   * animate columns; same-depth key changes refresh content in place.
   */
  transitionKey?: string;
  /**
   * When true, SlideLayout renders a React Router `<Outlet />` and exposes a
   * `SlidePanelRegistrar` on the outlet context, so child route elements can
   * register their columns via `useSlidePanel(...)` without the consuming
   * shell having to thread setters through `useOutletContext`.
   */
  enableChildRoutes?: boolean;
  /**
   * Additional fields to merge into the outlet context alongside the
   * `SlidePanelRegistrar`. Only used when `enableChildRoutes` is true.
   */
  outletContext?: object;
}
